/**
 * Mediclinic Digital Literacy API Helper
 * A utility class to interact with the Digital Literacy API
 */

import { API_CONFIG } from "../config/apiConfig.js"
export class MediclinicAPI {
	//constructor(environment = "dev") {
	constructor(environment = API_CONFIG.DEFAULT_ENVIRONMENT) {
		// Base URLs from the API specification
		this.baseUrls = {
			dev: "https://mediclinic-digital-literacy-dev-gae9ecdvfue5fqbs.southafricanorth-01.azurewebsites.net/api",
			qa: "https://mediclinic-digital-literacy-qa-bxg7drh4aebecgdj.southafricanorth-01.azurewebsites.net/api",
			prod: "https://mediclinic-digitial-literacy-fbewhnbjdwfba4hg.southafricanorth-01.azurewebsites.net/api",
		}

		// API tokens (replace these with your actual tokens)
		this.apiTokens = {
			dev: "f83c9e6d0517a5b541fb41b275fa511f72d337ce8b4729b4cb281fc1f8dbba525c383a0cef69c740e918f9b0a20ab805a1ac7e9ec3cb8cf2d93db8eb6731c08952e4a428b2a473caf2d202fce78c44e7881c5a9b4b7ed03e825416148411c63ceaf2e62f732fd4859595a7dea7f3e9fe53515f79a4fb1d2efef744db95342c05",
			qa: "7eb11a401d9b605864d1f7ed6817cc339d5bb6f7f2503f32f6800f67a57621634fef3ed76eb64056196d3900ef065a6ba7efb5f1c7070de283fffb161aae001e9fb3b1ab2d094baa06a9c0c68c8fd77854e9d2a3e39eebdc169cac21554058842983338cb0182e9c31bf6a1a2f8f0bae0363df021b3a1321dfcf7a13491495b2",
			prod: "5e90fcc989b63f8fcf9fd18df5da4383eca6aa76a6fe170e7323c3c7fc78b946e7c424d219db94d069723a94c239b9a977a55fba37d3eb785836d3b415587d25c39e94780e03c6b48b0dc67b19b218a2fe47eaf9a2e036c600955c533bc66a863237b9df3cf08c6ebbc51bfb1f4661b9bc1c122c76fb164f58ba8a49ea89a24d",
		}

		// Set the current environment
		this.setEnvironment(environment)
		console.log(`API environment set to ${environment}`)
	}

	/**
	 * Set the API environment
	 * @param {string} environment - 'dev', 'qa', or 'prod'
	 */
	setEnvironment(environment) {
		if (!["dev", "qa", "prod"].includes(environment)) {
			console.error("Invalid environment. Using dev as default.")
			environment = "dev"
		}

		this.baseUrl = this.baseUrls[environment]
		this.token = this.apiTokens[environment]
	}

	/**
	 * Get the authorization headers for API requests
	 * @returns {Object} Headers object
	 */
	getHeaders() {
		return {
			"Content-Type": "application/json",
			Authorization: `Bearer ${this.token}`,
		}
	}

	/**
	 * Get player progress
	 * @param {string} playerId - The player ID (7-13 digits)
	 * @returns {Promise<Array>} List of completed milestones
	 */
	async getPlayerProgress(playerId) {
		try {
			// Validate player ID format (7-13 digits)
			if (!this.validatePlayerId(playerId)) {
				throw new Error("Invalid player ID format. Must be 7-13 digits.")
			}

			const response = await fetch(`${this.baseUrl}/players/${playerId}`, {
				method: "GET",
				headers: this.getHeaders(),
			})

			if (response.status === 404) {
				// Player not found, return empty array
				return []
			}

			if (!response.ok) {
				throw new Error(`API error: ${response.status} ${response.statusText}`)
			}

			const data = await response.json()

			console.log("Player progress:", data)
			return data // Array of milestone IDs
		} catch (error) {
			console.error("Error fetching player progress:", error)
			throw error
		}
	}

	/**
	 * Record milestone completion
	 * @param {string} playerId - The player ID (7-13 digits)
	 * @param {string} milestoneId - The milestone ID to record as completed
	 * @returns {Promise<boolean>} Success status
	 */
	async recordMilestoneCompletion(playerId, milestoneId) {
		try {
			// Validate player ID format
			if (!this.validatePlayerId(playerId)) {
				throw new Error("Invalid player ID format. Must be 7-13 digits.")
			}

			const response = await fetch(`${this.baseUrl}/players`, {
				method: "POST",
				headers: this.getHeaders(),
				body: JSON.stringify({
					data: {
						playerId: playerId,
						completed: milestoneId,
					},
				}),
			})

			if (!response.ok) {
				throw new Error(`API error: ${response.status} ${response.statusText}`)
			}

			return true // Success
		} catch (error) {
			console.error("Error recording milestone completion:", error)
			throw error
		}
	}

	/**
	 * Get all player progress (admin function)
	 * @param {Object} options - Pagination and sorting options
	 * @returns {Promise<Object>} Player progress data with pagination
	 */
	async getAllPlayerProgress(options = {}) {
		try {
			const { page = 1, pageSize = 25, sort } = options

			let url = `${this.baseUrl}/players?populate=*`
			url += `&pagination[page]=${page}&pagination[pageSize]=${pageSize}`

			if (sort) {
				url += `&sort=${sort}`
			}

			const response = await fetch(url, {
				method: "GET",
				headers: this.getHeaders(),
			})

			if (!response.ok) {
				throw new Error(`API error: ${response.status} ${response.statusText}`)
			}

			return await response.json()
		} catch (error) {
			console.error("Error fetching all player progress:", error)
			throw error
		}
	}

	/**
	 * Record player rating
	 * @param {string} playerId - The player ID (7-13 digits)
	 * @param {number} rating - Rating value (1-5)
	 * @param {string} notes - Optional feedback notes
	 * @returns {Promise<boolean>} Success status
	 */
	async recordRating(playerId, rating, notes = "") {
		try {
			// Validate player ID format
			if (!this.validatePlayerId(playerId)) {
				throw new Error("Invalid player ID format. Must be 7-13 digits.")
			}

			// Validate rating
			if (rating < 1 || rating > 5 || !Number.isInteger(rating)) {
				throw new Error("Rating must be an integer between 1 and 5.")
			}

			const response = await fetch(`${this.baseUrl}/ratings`, {
				method: "POST",
				headers: this.getHeaders(),
				body: JSON.stringify({
					data: {
						playerId: playerId,
						rating: rating,
						notes: notes,
					},
				}),
			})

			if (!response.ok) {
				throw new Error(`API error: ${response.status} ${response.statusText}`)
			}

			return true // Success
		} catch (error) {
			console.error("Error recording rating:", error)
			throw error
		}
	}

	async recordCustomization(customizationData) {
		try {
			const res = await fetch(`${this.baseUrl}/customisations`, {
				method: "POST",
				headers: this.getHeaders(),
				body: JSON.stringify({
					data: customizationData,
				}),
			})

			if (!res.ok) {
				throw new Error(`API error: ${res.status} ${res.statusText}`)
			}
			return true // Success
		} catch (error) {
			throw error
		}
	}

	/**
	 * Validate player ID format (7-13 digits)
	 * @param {string} playerId - The player ID to validate
	 * @returns {boolean} Whether the ID is valid
	 */
	validatePlayerId(playerId) {
		const regex = /^[0-9]{7,13}$/
		return regex.test(playerId)
	}
}
